/** Formatos brasileiros usados nas telas. */

const percentual = new Intl.NumberFormat("pt-BR", {
  style: "percent",
  minimumFractionDigits: 0,
  maximumFractionDigits: 2,
});

/** Fração → "15%" / "2,5%". */
export function formatarPercentual(fracao: number | string | null | undefined): string {
  if (fracao === null || fracao === undefined || fracao === "") return "—";
  const n = typeof fracao === "string" ? Number(fracao) : fracao;
  return Number.isFinite(n) ? percentual.format(n) : "—";
}

/** "2026-10-16" → "16/10/2026" (sem passar por fuso horário). */
export function formatarData(iso: string | null | undefined): string {
  if (!iso) return "—";
  const [ano, mes, dia] = iso.slice(0, 10).split("-");
  return ano && mes && dia ? `${dia}/${mes}/${ano}` : iso;
}

/** Percentual digitado (15) ↔ fração gravada (0.15). */
export const percentualParaFracao = (valor: unknown): number | null => {
  if (valor === null || valor === undefined || valor === "") return null;
  const n = Number(String(valor).replace(",", "."));
  return Number.isFinite(n) ? Math.round(n * 1e6) / 1e8 : null;
};

export const fracaoParaPercentual = (valor: unknown): number | "" => {
  if (valor === null || valor === undefined || valor === "") return "";
  const n = Number(valor);
  return Number.isFinite(n) ? Math.round(n * 1e8) / 1e6 : "";
};

/**
 * Dias corridos de hoje (data local do navegador) até a data ISO informada.
 * Negativo = já passou. Conta em datas de calendário, sem efeito de horário de verão.
 */
export function diasAte(iso: string, hoje: Date = new Date()): number {
  const [ano, mes, dia] = iso.slice(0, 10).split("-").map(Number);
  const alvo = Date.UTC(ano ?? 0, (mes ?? 1) - 1, dia ?? 1);
  const base = Date.UTC(hoje.getFullYear(), hoje.getMonth(), hoje.getDate());
  return Math.round((alvo - base) / 86_400_000);
}

const moeda = new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL", minimumFractionDigits: 2, maximumFractionDigits: 4 });
const numero = new Intl.NumberFormat("pt-BR", { maximumFractionDigits: 4 });

/** Valor em reais com até 4 casas (custos unitários): 0.6238 → "R$ 0,6238"; 10 → "R$ 10,00". */
export function formatarMoeda(valor: number | string | null | undefined): string {
  if (valor === null || valor === undefined || valor === "") return "—";
  const n = Number(valor);
  return Number.isFinite(n) ? moeda.format(n) : "—";
}

/** Número com até 4 casas, sem zeros à direita: 10.0000 → "10". */
export function formatarNumero(valor: number | string | null | undefined): string {
  if (valor === null || valor === undefined || valor === "") return "—";
  const n = Number(valor);
  return Number.isFinite(n) ? numero.format(n) : "—";
}

/** Data e hora ISO no horário local: "20/10/2026 09:15". */
export function formatarDataHora(iso: string | null | undefined): string {
  if (!iso) return "—";
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? iso
    : d.toLocaleString("pt-BR", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });
}

/** Mesma normalização da coluna `busca` no banco (privado.normalizar_busca): minúsculas e sem acento. */
export function normalizarBusca(texto: unknown): string {
  const de = "áàâãäéèêëíìîïóòôõöúùûüçñ";
  const para = "aaaaaeeeeiiiiooooouuuucn";
  return String(texto ?? "")
    .toLowerCase()
    .split("")
    .map((c) => {
      const i = de.indexOf(c);
      return i >= 0 ? para[i] : c;
    })
    .join("");
}

/**
 * Telefone brasileiro com DDD, formatado enquanto se digita:
 * "79998172826" → "(79) 99817-2826"; "7932112345" → "(79) 3211-2345". Aceita no máximo 11 dígitos.
 */
export function formatarTelefone(valor: string | null | undefined): string {
  const d = String(valor ?? "").replace(/\D/g, "").slice(0, 11);
  if (d.length === 0) return "";
  if (d.length <= 2) return `(${d}`;
  const ddd = d.slice(0, 2);
  const resto = d.slice(2);
  if (resto.length <= 4) return `(${ddd}) ${resto}`;
  if (d.length <= 10) return `(${ddd}) ${resto.slice(0, 4)}-${resto.slice(4)}`;
  return `(${ddd}) ${resto.slice(0, 5)}-${resto.slice(5)}`;
}

/** Link de conversa no WhatsApp (wa.me) para um número brasileiro com DDD. Nulo se o número estiver incompleto. */
export function linkWhatsapp(numero: string | null | undefined): string | null {
  const d = String(numero ?? "").replace(/\D/g, "");
  return d.length === 10 || d.length === 11 ? `https://wa.me/55${d}` : null;
}
